import classnames from 'classnames';
import compose from 'lodash/fp/compose';
import sumBy from 'lodash/sumBy';
import { arrayOf, bool, number, string } from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';

import Tabs from '../../generic/Tabs';
import { InfoTooltip } from '../../generic/Tooltip';
import getInfoTooltip from '../../generic/Tooltip/get-info-tooltip';
import Titled from '../../layout/Titled';
import {
  opportunityBudgetsShape,
  opportunityStageCountShape,
} from '../../prop-types';
import api from '../../REST/api';
import withResolved from '../../REST/with-resolved';
import { rolesEnum } from '../../users/constants';
import { yesterdayString } from '../../utils/date-helpers';
import storedUserInfo from '../../utils/session-data/user-info';
import BudgetData from '../BudgetData';
import { allStagesEnum } from '../constants';
import HandoverButton from '../HandoverButton';
import HELP_TEXTS from '../help-texts';
import List from '../List';
import { allColumns, defaultListColumns } from '../opportunity-columns';

class ListCategorizedOpportunities extends React.PureComponent {
  state = {
    selectedOpportunities: [],
  };

  getSelectedItems = items => this.setState({ selectedOpportunities: items });

  getCount(stageValue, filter) {
    const { counts } = this.props;
    // in the backend the count is still called current
    const renamedFilter = filter === 'upcoming' ? 'current' : filter;
    const countObject = renamedFilter
      ? counts.find(count => count.stage === `${stageValue}-${renamedFilter}`)
      : counts.find(count => count.stage === stageValue);
    return countObject ? countObject.count : 0;
  }

  getQueryParams(stageValue, filter) {
    const {
      brandIds,
      relevantFor,
      relevantForSalesTeam,
      merchantIds,
      contactId,
      salesChannel,
    } = this.props;
    const options = {
      stage: stageValue,
      brandIds,
      relevantFor,
      relevantForSalesTeam,
      merchantIds,
      contactId,
      salesChannel,
    };
    if (filter === 'upcoming') {
      options.campaignStart_after = yesterdayString();
    }
    return options;
  }

  getAgreedUpcomingTabLabel() {
    return (
      <>
        {this.stageLabel(allStagesEnum.AGREED, 'upcoming')}
        {getInfoTooltip(HELP_TEXTS, 'currentAgreedStatus', 'navigation', 'top')}
      </>
    );
  }

  getAllTabLabel() {
    const { counts } = this.props;
    return `ALL (${sumBy(
      counts.filter(
        count =>
          ![
            `${allStagesEnum.AGREED.value}-current`, // still named like this in the backend
            `${allStagesEnum.AGREED.value}-archived`,
          ].includes(count.stage),
      ),
      'count',
    )})`;
  }

  stageLabel(stage, filter) {
    return filter
      ? `${stage.label} (${filter}) (${this.getCount(stage.value, filter)})`
      : `${stage.label} (${this.getCount(stage.value)})`;
  }

  prepareColumns(columns) {
    const { hiddenColumns } = this.props;
    return [
      ...columns.filter(
        col => !hiddenColumns.includes(col.Header || col.Label),
      ),
    ];
  }

  stageTab(stage) {
    const storedName = 'OPPORTUNITIES_FILTERS';
    const allOppColumns = this.prepareColumns(allColumns);
    const defaultColumns = this.prepareColumns(defaultListColumns);
    return {
      label: this.stageLabel(stage),
      value: stage.value,
      disabled: !this.getCount(stage.value),
      content: (
        <List
          storedName={storedName}
          setSelectedItems={this.getSelectedItems}
          searchParams={this.getQueryParams(stage.value)}
          columns={defaultColumns}
          allColumns={allOppColumns}
        />
      ),
    };
  }

  tabs() {
    const allOppColumns = this.prepareColumns(allColumns);
    const defaultColumns = this.prepareColumns(defaultListColumns);
    return [
      {
        label: this.getAllTabLabel(),
        value: 'all',
        content: (
          <List
            storedName="OPPORTUNITIES_FILTERS"
            setSelectedItems={this.getSelectedItems}
            searchParams={this.getQueryParams()}
            columns={defaultColumns}
            allColumns={allOppColumns}
            displayExportButton
          />
        ),
      },
      this.stageTab(allStagesEnum.PITCHED),
      this.stageTab(allStagesEnum.IN_TALKS),
      this.stageTab(allStagesEnum.SENT_PROPOSAL),
      this.stageTab(allStagesEnum.COMMIT_ON_BUDGET),
      this.stageTab(allStagesEnum.CLOSED_WON),
      {
        label: this.getAgreedUpcomingTabLabel(),
        value: `${allStagesEnum.AGREED.value}-upcoming`,
        disabled: !this.getCount(allStagesEnum.AGREED.value, 'upcoming'),
        content: (
          <List
            storedName="OPPORTUNITIES_FILTERS"
            setSelectedItems={this.getSelectedItems}
            searchParams={this.getQueryParams(
              allStagesEnum.AGREED.value,
              'upcoming',
            )}
            columns={defaultColumns}
            allColumns={allOppColumns}
          />
        ),
      },
      {
        label: this.stageLabel(allStagesEnum.AGREED),
        value: `${allStagesEnum.AGREED.value}`,
        content: (
          <List
            storedName="OPPORTUNITIES_FILTERS"
            setSelectedItems={this.getSelectedItems}
            searchParams={this.getQueryParams(allStagesEnum.AGREED.value)}
            columns={defaultColumns}
            allColumns={allOppColumns}
          />
        ),
      },
      this.stageTab(allStagesEnum.CLOSED_LOST),
    ];
  }

  render() {
    const { title, titleSize, showCreateLink, showBudgets, brandIds, budgets } =
      this.props;
    const currentUserInfo = storedUserInfo.get();
    const isSuperuser = !!currentUserInfo?.roles?.includes(rolesEnum.SUPERUSER);

    return (
      <Titled
        title={
          showBudgets ? (
            <BudgetData
              openBudget={budgets.openBudget}
              securedBudget={budgets.securedBudget}
              securedPushedBudget={budgets.securedPushedBudget}
            />
          ) : (
            title
          )
        }
        size={showBudgets ? 'reg' : titleSize}
        actions={
          showCreateLink && [
            <HandoverButton
              selectedItems={this.state.selectedOpportunities}
              itemsType="opportunities"
            />,
            <Link
              to={{
                pathname: '/opportunities/add',
                state: { brandIds },
              }}
              className={classnames('btn btn-secondary', {
                disabled: !isSuperuser,
                'authorized-link': true,
              })}
              query={api.addOpportunity}
              disabled={!isSuperuser}
            >
              Create Opportunity
              {!isSuperuser && (
                <InfoTooltip target="btn">
                  To create an Opportunity you should visit Salesforce.
                </InfoTooltip>
              )}
            </Link>,
          ]
        }
      >
        <Tabs
          tabs={this.tabs()}
          defaultActive={`${allStagesEnum.AGREED.value}-upcoming`}
          id="categorizedOpportunities"
        />
      </Titled>
    );
  }
}

ListCategorizedOpportunities.propTypes = {
  title: string,
  titleSize: string,
  hiddenColumns: arrayOf(string.isRequired),
  showCreateLink: bool,
  showBudgets: bool,
  counts: arrayOf(opportunityStageCountShape.isRequired).isRequired,
  brandIds: arrayOf(number),
  relevantFor: number,
  relevantForSalesTeam: string,
  merchantIds: number,
  contactId: number,
  budgets: opportunityBudgetsShape.isRequired,
  salesChannel: string,
};

ListCategorizedOpportunities.defaultProps = {
  title: 'Opportunities',
  titleSize: undefined,
  hiddenColumns: [],
  showCreateLink: true,
  showBudgets: false,
  brandIds: [],
  relevantFor: undefined,
  relevantForSalesTeam: undefined,
  merchantIds: undefined,
  contactId: undefined,
  salesChannel: undefined,
};

export default compose(
  withResolved({
    query: api.getOpportunitiesCount,
    deriveQueryArgs: ({
      brandIds,
      relevantFor,
      relevantForSalesTeam,
      merchantIds,
      contactId,
      salesChannel,
    }) => ({
      search: {
        brandIds,
        relevantFor,
        relevantForSalesTeam,
        merchantIds,
        contactId,
        salesChannel,
      },
    }),
    as: 'counts',
  }),
  withResolved({
    query: api.getOpportunityBudgets,
    deriveQueryArgs: ({ relevantFor, relevantForSalesTeam }) => ({
      search: { relevantFor, relevantForSalesTeam },
    }),
    as: 'budgets',
  }),
)(ListCategorizedOpportunities);
